import { useEffect, useState } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import {
  FileText, FileCheck2, File, Download, Eye, Loader2, FolderOpen,
  Clock, ShieldCheck,
} from "lucide-react";

interface PortailDocument {
  id: string;
  nom: string;
  type: string;
  dateUpload: string | null;
  signedUrl: string;
  fichierUrl: string;
}

const TYPE_LABELS: Record<string, string> = {
  contrat: "Contrats",
  autorisation: "Autorisations",
  diplome: "Diplômes & Certifications",
  accord: "Accords",
  autre: "Autres documents",
};

const SECTIONS = [
  {
    key: "contrat",
    label: "Contrats",
    icon: FileCheck2,
    iconColor: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    description: "Contrats de revendeur, contrats d'apporteur d'affaires",
  },
  {
    key: "autorisation",
    label: "Autorisations",
    icon: ShieldCheck,
    iconColor: "text-violet-600 dark:text-violet-400",
    bgColor: "bg-violet-50 dark:bg-violet-900/20",
    description: "Autorisation revendeur officielle Pudu et autres habilitations",
  },
  {
    key: "autres",
    label: "Autres documents",
    icon: FolderOpen,
    iconColor: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
    description: "Certifications, guides, documents marketing, etc.",
    types: ["diplome", "accord", "autre"],
  },
];

function fileIcon(url: string) {
  const lower = url.toLowerCase();
  if (lower.includes(".pdf")) return { Icon: FileText, color: "text-red-500" };
  if (lower.includes(".doc") || lower.includes(".docx")) return { Icon: FileText, color: "text-blue-500" };
  if (lower.match(/\.(jpg|jpeg|png|gif|webp)/)) return { Icon: FileText, color: "text-green-500" };
  return { Icon: File, color: "text-muted-foreground" };
}

function formatDate(d: string | null) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return d; }
}

function isPdf(url: string) {
  return url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("pdf");
}

function DocCard({ doc }: { doc: PortailDocument }) {
  const { Icon, color } = fileIcon(doc.fichierUrl);
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-secondary/30 transition-colors">
      <div className={`shrink-0 ${color}`}>
        <Icon className="w-8 h-8" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{doc.nom}</p>
        {doc.dateUpload && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />
            Déposé le {formatDate(doc.dateUpload)}
          </p>
        )}
      </div>
      <div className="flex gap-1.5 shrink-0">
        {isPdf(doc.fichierUrl) && (
          <a
            href={doc.signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            title="Aperçu"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Aperçu</span>
          </a>
        )}
        <a
          href={doc.signedUrl}
          download
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          title="Télécharger"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Télécharger</span>
        </a>
      </div>
    </div>
  );
}

function DocSection({
  section,
  docs,
}: {
  section: typeof SECTIONS[number];
  docs: PortailDocument[];
}) {
  const { icon: _, ...rest } = section as any;
  const { Icon } = section;
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
        <div className={`p-1.5 rounded-lg ${section.bgColor}`}>
          <Icon className={`w-4 h-4 ${section.iconColor}`} />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-foreground">{section.label}</h2>
          <p className="text-xs text-muted-foreground">{section.description}</p>
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          {docs.length} document{docs.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="p-4">
        {docs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Aucun document dans cette catégorie</p>
        ) : (
          <div className="space-y-2">
            {docs.map(doc => <DocCard key={doc.id} doc={doc} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const { brand } = usePortailAuth();
  const { refreshUnreadCount } = usePortailRouter();
  const [docs, setDocs] = useState<PortailDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authFetch("/api/portail/documents")
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setDocs(data);
        else setError(data.message || "Erreur");
      })
      .catch(() => setError("Impossible de charger les documents"))
      .finally(() => setLoading(false));

    // Marquer les notifications document comme lues
    authFetch("/api/portail/notifications/read-docs", { method: "PUT" })
      .then(() => refreshUnreadCount())
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-center text-sm text-red-500">{error}</div>;
  }

  const totalDocs = docs.length;

  const docsFor = (section: typeof SECTIONS[number]) => {
    if (section.types) return docs.filter(d => section.types!.includes(d.type));
    return docs.filter(d => d.type === section.key);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Documents contractuels</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {totalDocs === 0
            ? "Aucun document disponible pour le moment."
            : `${totalDocs} document${totalDocs > 1 ? "s" : ""} disponible${totalDocs > 1 ? "s" : ""}`}
        </p>
      </div>

      {totalDocs === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">Aucun document disponible pour le moment</p>
          <p className="text-xs text-muted-foreground">Contactez votre responsable Jobotto pour obtenir vos documents contractuels.</p>
        </div>
      ) : (
        SECTIONS.map(section => (
          <DocSection key={section.key} section={section} docs={docsFor(section)} />
        ))
      )}
    </div>
  );
}
